/** The slice of a Postgres client the engine needs; PGlite (worker or direct) satisfies it. */
export interface LocalDb {
  query<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<{ rows: T[] }>;
  exec(sql: string): Promise<unknown>;
  transaction<T>(fn: (tx: Tx) => Promise<T>): Promise<T>;
}

export interface Tx {
  query<T = Record<string, unknown>>(sql: string, params?: unknown[]): Promise<{ rows: T[] }>;
}

export type Row = Record<string, unknown>;

export interface PushChange {
  changeId: string;
  table: string;
  op: 'upsert' | 'delete';
  id: string;
  baseRev: number;
  row?: Row;
}

export type PushResult =
  | { changeId: string; status: 'applied'; rev: number }
  | { changeId: string; status: 'conflict'; rev: number; serverRow: Row }
  | { changeId: string; status: 'rejected'; code: string; message: string };

export interface PullPage {
  rows: { table: string; row: Row }[];
  cursor: number;
  hasMore: boolean;
  orgRev: number;
}

/** The server, as the engine sees it. Implementations translate HTTP failures into the errors below. */
export interface Remote {
  push(changes: PushChange[]): Promise<{ results: PushResult[]; orgRev: number }>;
  pull(since: number, limit: number): Promise<PullPage>;
  /** The server's current version of one row, or null if it does not exist. */
  getRow(table: string, id: string): Promise<Row | null>;
  putFile(id: string, bytes: Uint8Array, mime: string): Promise<void>;
  getFile(id: string): Promise<Uint8Array | null>;
}

/** No network, or the server could not be reached. Work continues offline. */
export class OfflineError extends Error {
  constructor(message = 'Offline') {
    super(message);
    this.name = 'OfflineError';
  }
}

/** The server refused the session. `revoked` means this device was removed from the lab. */
export class AuthError extends Error {
  constructor(
    readonly reason: 'expired' | 'revoked',
    message = 'Signed out',
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'signed_out';

export interface SyncStatus {
  state: SyncState;
  /** Local changes not yet accepted by the server. */
  pending: number;
  /** Changes the server refused or that lost to a newer version, not yet reviewed. */
  conflicts: number;
  lastSyncedAt: string | null;
  error?: string;
  authReason?: 'expired' | 'revoked';
}
